/**
 * Cloche des notifications.
 *
 * Signale au personnel un fait qu'il doit connaitre et que personne ne lui a
 * dit. Deux faits aujourd'hui : une table abandonnee dont la session a expire
 * en laissant des commandes impayees, et une commande immobilisee trop
 * longtemps a une etape alors que le service continue.
 *
 * La pastille compte les non lues du restaurant, pas de l'utilisateur : dans
 * une salle, celui qui lit traite pour tout le monde.
 *
 * C'est aussi d'ici, et seulement d'ici, que le personnel ecarte les commandes
 * d'une table abandonnee : l'action annule chaque commande encore en cours par
 * POST /admin/sessions/:id/discard-orders. Elle n'est jamais automatique, une
 * assiette servie et jamais payee etant une perte que le restaurant doit voir
 * et decider lui-meme.
 *
 * Une commande immobilisee laisse le choix : l'annuler, ou la garder. Garder
 * n'est pas ignorer -- c'est une decision, qui eteint l'alerte de cet episode
 * sans toucher a la commande. Sans ce choix, la cloche redirait la meme chose
 * a chaque passage et le personnel finirait par ne plus la lire.
 *
 * Voir docs/adr/0016-ecarter-les-commandes-d-une-table-abandonnee.md pour
 * l'ecartement, docs/adr/0017-alerter-sur-une-commande-immobilisee.md pour
 * l'immobilisation, docs/adr/0013-cloture-de-session-au-service-solde.md pour
 * la cloture qui declenche la premiere.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DiscardAbandonedOrdersResponse,
  NotificationDTO,
  NotificationListResponse,
  OrderStalledNotification,
  SessionExpiredUnpaidNotification,
} from '@scan2eat/shared';
import { ABANDONED_SESSION_CANCEL_REASON, formatMoney } from '@scan2eat/shared';

import { ApiError, apiFetch } from '../api/client.js';
import { useAuth } from '../auth/context.js';
import { labels } from '../labels.js';
import { getAdminSocket, type AdminSocket } from '../realtime/socket.js';
import { CancelDialog } from './CancelDialog.js';
import { DropdownMenu } from './DropdownMenu.js';
import { Icon } from './Icon.js';
import styles from './NotificationBell.module.css';

/**
 * Traduit l'echec d'un ecartement en une phrase utile.
 *
 * Le cas courant est le refus de la machine a etats : `assertTransition` reclame
 * `orders.cancel.late` des qu'une commande a depasse PENDING, donc un STAFF
 * devant une table servie recoit FORBIDDEN. Lui montrer le code brut ne lui dit
 * pas quoi faire ; lui dire d'appeler un responsable, si.
 */
function discardErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return labels.notifications.discardFailed;
  switch (error.code) {
    case 'FORBIDDEN':
      return labels.notifications.discardForbidden;
    case 'SESSION_STILL_OPEN':
      return labels.notifications.discardSessionOpen;
    case 'NOT_FOUND':
    case 'ORDER_IMMUTABLE':
    case 'ORDER_INVALID_TRANSITION':
      return labels.notifications.discardConflict;
    default:
      return labels.notifications.discardFailed;
  }
}

/**
 * Traduit l'echec d'une annulation de commande immobilisee.
 *
 * Meme partage de role que partout ailleurs : `orders.cancel.late` des que la
 * commande a depasse PENDING. Le conflit est ici tres plausible -- la commande
 * a pu avancer entre l'emission de l'alerte et le clic.
 */
function cancelErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return labels.notifications.stalledCancelFailed;
  switch (error.code) {
    case 'FORBIDDEN':
      return labels.notifications.stalledCancelForbidden;
    case 'NOT_FOUND':
    case 'ORDER_IMMUTABLE':
    case 'ORDER_INVALID_TRANSITION':
      return labels.notifications.stalledCancelConflict;
    default:
      return labels.notifications.stalledCancelFailed;
  }
}

export function NotificationBell() {
  const { auth } = useAuth();
  // Les notifications appartiennent a un restaurant. Un administrateur de
  // plateforme n'en a pas tant qu'il n'a pas bascule sur l'un d'eux : la
  // requete repondrait FORBIDDEN. Voir ADR 0009.
  const hasRestaurant = Boolean(auth?.restaurant);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationDTO[]>([]);
  const [unread, setUnread] = useState(0);
  /** Notification dont on ecarte les commandes, donc dialogue ouvert. */
  const [discarding, setDiscarding] = useState<SessionExpiredUnpaidNotification | null>(null);
  /** Commande immobilisee que l'on s'apprete a annuler. */
  const [cancelling, setCancelling] = useState<OrderStalledNotification | null>(null);
  const [discardPending, setDiscardPending] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [discardResult, setDiscardResult] = useState<string | null>(null);
  // Notifications traitees pendant que le panneau est ouvert. Le detail est
  // fige a l'emission : il ne sait pas que la commande vient d'etre annulee ou
  // gardee. Sans cette trace, la ligne continue de proposer une action faite.
  const [handled, setHandled] = useState<ReadonlySet<string>>(new Set());
  const buttonRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!hasRestaurant) return;
    try {
      const data = await apiFetch<NotificationListResponse>('/admin/notifications');
      setItems(data.notifications);
      setUnread(data.unreadCount);
    } catch {
      /* La cloche ne doit jamais faire tomber la barre de navigation. */
    }
  }, [hasRestaurant]);

  /**
   * S'accroche au canal admin des qu'il existe, et sonde une fois par minute.
   *
   * La barre de navigation vit sur toutes les pages, mais c'est la page
   * affichee qui ouvre la connexion : le socket peut donc apparaitre apres le
   * montage. Le sondage sert de plancher, et suffit largement -- une table
   * abandonnee est signalee vingt-quatre heures apres les faits.
   */
  useEffect(() => {
    let attached: AdminSocket | null = null;
    const onCreated = () => { void load(); };

    const sync = () => {
      const current = getAdminSocket();
      if (current !== attached) {
        attached?.off('notification:created', onCreated as never);
        current?.on('notification:created', onCreated as never);
        attached = current;
      }
      void load();
    };

    sync();
    const timer = window.setInterval(sync, 60_000);
    return () => {
      window.clearInterval(timer);
      attached?.off('notification:created', onCreated as never);
    };
  }, [load]);

  /**
   * Ecarte les commandes restees en cours sur la session abandonnee.
   *
   * L'echec garde le dialogue ouvert et y affiche la raison : le panneau ne
   * doit ni disparaitre ni laisser croire que l'action a eu lieu. Le succes
   * recharge la liste et la pastille, l'API marquant au passage la notification
   * de cette session comme lue.
   */
  const discardOrders = async (note: string) => {
    if (!discarding) return;
    setDiscardPending(true);
    setDiscardError(null);
    try {
      const data = await apiFetch<DiscardAbandonedOrdersResponse>(
        `/admin/sessions/${discarding.details.sessionId}/discard-orders`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Le motif est facultatif : sans lui, l'API pose
          // ABANDONED_SESSION_CANCEL_REASON.
          body: JSON.stringify(note ? { note } : {}),
        },
      );
      const notificationId = discarding.id;
      setDiscarding(null);
      setHandled((seen) => new Set(seen).add(notificationId));
      // Un compte nul n'est pas une erreur : l'appel est idempotent, quelqu'un
      // d'autre a pu traiter la table entre-temps.
      setDiscardResult(
        data.cancelledCount > 0
          ? labels.notifications.discardDone(data.cancelledCount)
          : labels.notifications.discardNone,
      );
      await load();
    } catch (e) {
      setDiscardError(discardErrorMessage(e));
    } finally {
      setDiscardPending(false);
    }
  };

  const openDiscardDialog = (n: SessionExpiredUnpaidNotification) => {
    setDiscardError(null);
    setDiscardResult(null);
    setDiscarding(n);
  };

  /**
   * Annule une commande immobilisee, par la route de transition ordinaire.
   *
   * Rien de specifique cote serveur : c'est l'annulation que le kanban propose
   * deja, atteinte depuis l'alerte plutot que depuis la carte. Le motif est
   * obligatoire, comme pour toute annulation.
   */
  const cancelStalledOrder = async (note: string) => {
    if (!cancelling) return;
    setDiscardPending(true);
    setDiscardError(null);
    try {
      await apiFetch(`/admin/orders/${cancelling.details.orderId}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'CANCEL', note }),
      });
      const notificationId = cancelling.id;
      const orderNumber = cancelling.details.number;
      setCancelling(null);
      setHandled((seen) => new Set(seen).add(notificationId));
      setDiscardResult(labels.notifications.stalledCancelDone(orderNumber));
      // L'alerte a rempli son office : elle sort de la cloche.
      await markNotificationRead(notificationId);
      await load();
    } catch (e) {
      setDiscardError(cancelErrorMessage(e));
    } finally {
      setDiscardPending(false);
    }
  };

  const openCancelDialog = (n: OrderStalledNotification) => {
    setDiscardError(null);
    setDiscardResult(null);
    setCancelling(n);
  };

  const markNotificationRead = async (notificationId: string) => {
    try {
      await apiFetch(`/admin/notifications/${notificationId}/read`, { method: 'POST' });
    } catch {
      /* La commande a bien ete traitee : l'alerte non eteinte est un moindre mal. */
    }
  };

  /**
   * Garder la commande telle quelle.
   *
   * Ce n'est pas ignorer l'alerte : c'est y repondre. La notification est lue,
   * la commande ne bouge pas, et la tache ne resignalera pas cet episode --
   * seulement un nouveau blocage, plus loin dans le parcours.
   */
  const keepStalledOrder = async (n: OrderStalledNotification) => {
    setDiscardError(null);
    setHandled((seen) => new Set(seen).add(n.id));
    setDiscardResult(labels.notifications.stalledKeptDone(n.details.number));
    await markNotificationRead(n.id);
    await load();
  };

  const markAllRead = async () => {
    try {
      await apiFetch('/admin/notifications/read-all', { method: 'POST' });
      await load();
    } catch {
      /* noop */
    }
  };

  const openPanel = () => {
    setOpen((prev) => !prev);
    setDiscardResult(null);
    void load();
  };

  if (!hasRestaurant) return null;

  return (
    <div className={styles.wrap}>
      <button
        ref={buttonRef}
        type="button"
        onClick={openPanel}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={
          unread > 0 ? labels.notifications.ariaWithCount(unread) : labels.notifications.aria
        }
        className="btn-icon btn-icon--quiet"
      >
        <Icon name="bell" />
        {unread > 0 && (
          <span aria-hidden="true" className={styles.badge}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      <DropdownMenu open={open} onClose={() => setOpen(false)} anchorRef={buttonRef} width="min(24rem, calc(100vw - 16px))">
        <div className={styles.header}>
          <strong>{labels.notifications.title}</strong>
          {unread > 0 && (
            <button type="button" onClick={markAllRead} className="btn-link">{labels.notifications.markAllRead}</button>
          )}
        </div>

        {discardResult && (
          <p role="status" className={styles.result}>
            <Icon name="check-circle" size={16} />
            {discardResult}
          </p>
        )}

        {items.length === 0 ? (
          <div className={styles.empty}>
            <span className="icon-circle icon-circle--sm"><Icon name="bell" size={18} /></span>
            <p>{labels.notifications.empty}</p>
          </div>
        ) : (
          <ul className={styles.list}>
            {items.map((n) => (
              <li key={n.id} className={`${styles.item} ${n.readAt ? '' : styles.unread}`}>
                <span className={`icon-circle icon-circle--sm ${styles.itemIcon}`}>
                  <Icon name={n.type === 'ORDER_STALLED' ? 'clock' : 'alert'} size={18} />
                </span>
                <div className="grow">
                  {n.type === 'ORDER_STALLED' ? (
                    <>
                      <p className={styles.title}>
                        {labels.notifications.stalledTitle(n.details.number, n.details.tableLabel)}
                      </p>
                      <p className={styles.body}>
                        {labels.notifications.stalledBody(
                          labels.status[n.details.status],
                          n.details.stalledMinutes,
                        )}
                      </p>
                      <p className={styles.meta}>
                        {formatMoney(n.details.totalMinor, n.details.currency)}
                        {' · '}
                        {stamp(n.createdAt)}
                      </p>
                      {handled.has(n.id) ? (
                        <p className={styles.handled}><Icon name="check" size={14} />{labels.notifications.stalledHandled}</p>
                      ) : (
                        <div className={styles.actions}>
                          <button
                            type="button"
                            onClick={() => openCancelDialog(n)}
                            aria-label={labels.notifications.stalledCancelAria(n.details.number)}
                            className="btn-danger btn-sm"
                          >
                            {labels.notifications.stalledCancel}
                          </button>
                          <button
                            type="button"
                            onClick={() => void keepStalledOrder(n)}
                            aria-label={labels.notifications.stalledKeepAria(n.details.number)}
                            className="btn-ghost btn-sm"
                          >
                            {labels.notifications.stalledKeep}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className={styles.title}>
                        {labels.notifications.unpaidTitle(n.details.tableLabel)}
                      </p>
                      <p className={styles.body}>
                        {labels.notifications.unpaidBody(
                          n.details.orders.length,
                          formatMoney(n.details.unpaidTotalMinor, n.details.currency),
                        )}
                      </p>
                      <p className={styles.meta}>
                        {n.details.orders.map((o) => `#${o.number}`).join(', ')}
                        {' · '}
                        {stamp(n.createdAt)}
                      </p>
                      {handled.has(n.id) ? (
                        <p className={styles.handled}><Icon name="check" size={14} />{labels.notifications.discardHandled}</p>
                      ) : (
                        <div className={styles.actions}>
                          <button
                            type="button"
                            onClick={() => openDiscardDialog(n)}
                            aria-label={labels.notifications.discardAria(n.details.tableLabel)}
                            className="btn-danger btn-sm"
                          >
                            {labels.notifications.discard}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenu>

      {cancelling && (
        <CancelDialog
          title={labels.notifications.stalledCancelTitle(cancelling.details.number)}
          description={labels.notifications.stalledCancelDescription(
            labels.status[cancelling.details.status],
            cancelling.details.stalledMinutes,
          )}
          confirmLabel={labels.notifications.stalledCancelConfirm}
          abortLabel={labels.notifications.discardAbort}
          pending={discardPending}
          error={discardError}
          onConfirm={(note) => void cancelStalledOrder(note)}
          onCancel={() => setCancelling(null)}
        />
      )}

      {discarding && (
        <CancelDialog
          title={labels.notifications.discardTitle(discarding.details.tableLabel)}
          description={labels.notifications.discardDescription(discarding.details.orders.length)}
          reasonLabel={labels.notifications.discardReasonLabel}
          reasonHint={labels.notifications.discardReasonHint(ABANDONED_SESSION_CANCEL_REASON)}
          reasonRequired={false}
          confirmLabel={labels.notifications.discardConfirm}
          abortLabel={labels.notifications.discardAbort}
          pending={discardPending}
          error={discardError}
          onConfirm={(note) => { void discardOrders(note); }}
          onCancel={() => { if (!discardPending) setDiscarding(null); }}
        />
      )}
    </div>
  );
}

/** Jour et heure d'emission, format court francais. */
function stamp(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}
